import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

const CHECK_INTERVAL_MS = 10000;
const REQUEST_TIMEOUT_MS = 8000;
const SLOW_REQUEST_MS = 1500;
const RECOVERY_DURATION_MS = 3000;

export function useConnectionStatus() {
    const status = ref('unknown');
    const recovered = ref(false);
    const banner = computed(() => {
        if (status.value === 'offline') return 'offline';
        if (recovered.value) return 'online';
        return status.value === 'poor' ? 'poor' : null;
    });
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    let interval;
    let recoveryTimer;
    let request = null;
    let stopped = false;
    let lastDuration = 0;

    function updateStatus(next) {
        const wasOffline = status.value === 'offline';
        status.value = next;
        if (next === 'offline') {
            clearTimeout(recoveryTimer);
            recovered.value = false;
        } else if (wasOffline) {
            recovered.value = true;
            recoveryTimer = setTimeout(() => { recovered.value = false; }, RECOVERY_DURATION_MS);
        }
    }

    function quality() {
        return lastDuration >= SLOW_REQUEST_MS ||
            ['slow-2g', '2g', '3g'].includes(connection?.effectiveType) ||
            connection?.rtt >= 450 ||
            (connection?.downlink > 0 && connection.downlink <= 1.5)
            ? 'poor' : 'online';
    }

    function cancelRequest() {
        if (!request) return;
        clearTimeout(request.timeout);
        request.controller.abort();
        request = null;
    }

    function onOffline() {
        cancelRequest();
        updateStatus('offline');
    }

    async function check() {
        if (stopped || document.visibilityState === 'hidden') return;
        if (navigator.onLine === false) {
            onOffline();
            return;
        }
        if (request) return;
        const controller = new AbortController();
        const current = {
            controller,
            timeout: setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS),
        };
        request = current;
        const startedAt = performance.now();
        try {
            const url = new URL(`${process.env.BASE_URL || '/'}qr-connectivity.txt`, window.location.origin);
            url.searchParams.set('_', Date.now());
            const response = await fetch(url.href, {
                cache: 'no-store',
                credentials: 'omit',
                redirect: 'error',
                signal: controller.signal,
            });
            // Validate the payload so a captive portal or SPA fallback cannot report success.
            if (!response.ok || (await response.text()).trim() !== 'qr-connectivity-ok') {
                throw new Error('Connectivity probe did not return the expected response');
            }
            if (request !== current || stopped) return;
            lastDuration = performance.now() - startedAt;
            updateStatus(quality());
        } catch (error) {
            if (request === current && !stopped) updateStatus('offline');
        } finally {
            clearTimeout(current.timeout);
            if (request === current) request = null;
        }
    }

    function onConnectionChange() {
        if (status.value === 'online' || status.value === 'poor') status.value = quality();
        check();
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            cancelRequest();
        } else {
            check();
        }
    }

    onMounted(() => {
        window.addEventListener('offline', onOffline);
        window.addEventListener('online', check);
        document.addEventListener('visibilitychange', onVisibilityChange);
        connection?.addEventListener('change', onConnectionChange);
        interval = setInterval(check, CHECK_INTERVAL_MS);
        check();
    });

    onBeforeUnmount(() => {
        stopped = true;
        clearInterval(interval);
        clearTimeout(recoveryTimer);
        cancelRequest();
        window.removeEventListener('offline', onOffline);
        window.removeEventListener('online', check);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        connection?.removeEventListener('change', onConnectionChange);
    });

    return { banner };
}
